import {useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {differenceInDays} from '../services/dateUtils'
import {
  selectSearchParams,
  setSearchParams
} from '../store/slices/searchParamsSlice'
import {AppView, SearchParams} from '../schema'
import {setAppView} from '../store/slices/appViewSlice'
import {getLogger} from '../services/logging'
import {selectConfig, selectPropertiesById} from '../store/slices/configSlice'
import {submitSearch} from '../store/slices/searchSubmittedSlice'

const logger = getLogger('useInitSearchParams')

const safeParseInt = (value: string | undefined): number => {
  const parsed = parseInt(value || '0', 10)
  return isNaN(parsed) ? 0 : parsed
}

export interface QueryStringSearchParams {
  location?: string
  region?: string
  propertyId?: string
  arrival?: string
  departure?: string
  adults?: string
  promoCode?: string
  corporateCode?: string
  hasAAA?: string
  hasAARP?: string
  hasGov?: string
  hasMilitary?: string
}

const queryStringKeys = [
  'location',
  'region',
  'propertyId',
  'arrival',
  'departure',
  'adults',
  'promoCode',
  'corporateCode',
  'hasAAA',
  'hasAARP',
  'hasGov',
  'hasMilitary'
]

/**
 * Case insensitive check for 'all'
 * @param value
 * @returns
 */
function isAll(value: string | undefined): boolean {
  return value ? value.toLowerCase() === 'all' : false
}

/**
 * Computes the property parameter based on the URL and existing search parameters
 * @param propertyId
 * @param searchParams
 * @param propertiesById
 * @returns
 */
function computePropertyParam(
  propertyId: string | undefined,
  searchParams: SearchParams,
  propertiesById: Record<string, any>
): string {
  const decodedPropertyId = propertyId
    ? decodeURIComponent(propertyId)
    : undefined
  if (!decodedPropertyId || isAll(decodedPropertyId)) {
    // no parameter in URL - default existing propertyId or all
    return searchParams.propertyId || 'all'
  } else {
    // parameter in URL - check if it is valid, default to all
    const propertyIds = Object.keys(propertiesById)
    return propertyIds.includes(decodedPropertyId) ? decodedPropertyId : 'all'
  }
}

/**
 * Maps legacy location ID formats to new format
 * @param locationIds
 * @returns
 */
function getLegacyLocationMappings(
  locationIds: string[]
): Record<string, string> {
  return locationIds.reduce(
    (acc: Record<string, string>, locationId: string) => {
      const legacyLocationId =
        locationId !== 'Washington, DC' ? locationId.split(',')[0] : locationId
      acc[legacyLocationId] = locationId
      return acc
    },
    {} as Record<string, string>
  )
}

/**
 * Computes the location parameter based on the URL and existing search parameters
 * @param locationId
 * @param propertyId
 * @param searchParams
 * @param locationsAndRegions
 * @param propertiesById
 * @returns
 */
function computeLocationParam(
  locationId: string | undefined,
  propertyId: string | undefined,
  searchParams: SearchParams,
  locationsAndRegions: string[],
  propertiesById: Record<string, any>
): string {
  const decodedLocation = locationId
    ? decodeURIComponent(locationId)
    : undefined

  // no property parameter and location parameter in URL - check if it is valid
  if (decodedLocation) {
    // check if locationId is valid (new format)
    if (locationsAndRegions.includes(decodedLocation)) {
      return decodedLocation
    }

    // check if locationId is valid (legacy format)
    const legacyLocationMappings = getLegacyLocationMappings(
      locationsAndRegions
    )
    const mappedLocationId = legacyLocationMappings[decodedLocation]
    if (mappedLocationId) {
      return mappedLocationId
    }
  }

  // property parameter in URL - check if it is valid, set property location
  if (propertyId && !isAll(propertyId)) {
    const property = propertiesById[propertyId]
    if (property) {
      return property.location
    }
  }

  // no parameter in URL - default existing location or all
  return searchParams.location || ''
}

const useInitSearchParams = () => {
  const dispatch = useDispatch()
  const searchParams = useSelector(selectSearchParams)
  const config = useSelector(selectConfig)
  const propertiesById = useSelector(selectPropertiesById)

  useEffect(() => {
    try {
      if (config && propertiesById) {
        const queryString = window.location.search
        const urlParams = new URLSearchParams(queryString)

        const hashParams = new URLSearchParams(
          (window.location.hash.match(/\?(.*)/) || [])[1] || ''
        )

        for (const [key, value] of hashParams.entries()) {
          if (!urlParams.has(key)) {
            const specialRateKeys = [
              'promoCode',
              'corporateCode',
              'hasAAA',
              'hasAARP',
              'hasGov',
              'hasMilitary'
            ]
            if (!specialRateKeys.includes(key)) {
              urlParams.append(key, value)
            }
          }
        }

        const queryStringParams: QueryStringSearchParams = {}
        urlParams.forEach((value, key) => {
          if (queryStringKeys.includes(key)) {
            queryStringParams[key as keyof QueryStringSearchParams] = value
          }
        })

        logger.info({
          message: 'Initializing search parameters...',
          data: {queryStringParams, searchParams}
        })

        if (Object.keys(queryStringParams).length > 0) {
          const {
            arrival,
            departure,
            region,
            location,
            propertyId,
            adults,
            promoCode,
            corporateCode,
            hasAAA,
            hasAARP,
            hasGov,
            hasMilitary
          } = queryStringParams

          const propertyParam = computePropertyParam(
            propertyId,
            searchParams,
            propertiesById
          )

          const locationsAndRegions = [...config.locations, ...config.regions]
          const locationParam = computeLocationParam(
            location || region,
            propertyParam,
            searchParams,
            locationsAndRegions,
            propertiesById
          )

          const startParam = arrival || searchParams.dateRange.start
          const endParam = departure || searchParams.dateRange.end
          const nightsParam =
            startParam && endParam ? differenceInDays(endParam, startParam) : 0
          const guestsParam = adults
            ? safeParseInt(adults)
            : searchParams.guests

          const updatedSearchParams: SearchParams = {
            ...searchParams,
            location: locationParam,
            propertyId: propertyParam,
            dateRange: {
              type: 'Specific',
              start: startParam,
              end: endParam,
              nights: nightsParam
            },
            guests: guestsParam,
            specialRates: {
              promoCode,
              corporateCode: promoCode ? undefined : corporateCode, // only set corporateCode if promoCode is not set
              hasAAA: hasAAA === 'true',
              hasAARP: hasAARP === 'true',
              hasGov: hasGov === 'true',
              hasMilitary: hasMilitary === 'true'
            }
          }

          logger.info({
            message:
              'Setting search parameters from URL and existing parameters...',
            data: {updatedSearchParams}
          })

          dispatch(setSearchParams(updatedSearchParams))

          // set app view based on search parameters
          if (
            updatedSearchParams.propertyId &&
            !isAll(updatedSearchParams.propertyId)
          ) {
            // propertyId is set - set unit results view
            dispatch(setAppView(AppView.CHOOSE_UNIT))
            dispatch(submitSearch())
          } else if (
            updatedSearchParams.location &&
            updatedSearchParams.location !== ''
          ) {
            // location is set - set property results view
            dispatch(setAppView(AppView.CHOOSE_PROPERTY))
            dispatch(submitSearch())
          } else {
            // default to home view
            dispatch(setAppView(AppView.HOME))
          }
        }
      }
    } catch (e) {
      logger.error({
        message: 'Error initializing search parameters from URL',
        errorData: {error: e instanceof Error ? e.message : 'Unknown error'}
      })
      return
    }
  }, [propertiesById])
}

export default useInitSearchParams
